<template>
    <div class="modal">
        <div class="modal-container" v-if="orderObject">
            <div class="modal-header">
                <close-icon v-on:click="closeView"></close-icon>
                <h1>Order {{ orderObject.reference }}</h1>
            </div>

            <div class="modal-body no-pad">
                <div class="flex">
                    <div class="w-full md:w-1/2 md:pr-4">
                        <div class="account-body">
                            <div class="form-field">
                                <label>Date/Time</label>
                                <input type="text" :value="formatDateShort(orderObject.created_at)" readonly="readonly">
                            </div>

                            <div class="form-field">
                                <label>Completed</label>
                                <input type="text" :value="formatDateShort(orderObject.complete)" readonly="readonly">
                            </div>

                            <div class="form-field">
                                <label>Sale Attribution</label>
                                <input type="text" readonly="readonly" :value="getSaleAttribution" v-on:dblclick.alt="handleChangeOrderAttribution" v-if="!changingAttribution">
                                <input type="text" v-model="orderObject.attribution_id" v-if="changingAttribution" @keydown.enter="changeOrderAttribution">
                            </div>

                            <div class="form-field" v-if="orderObject.created_from">
                                <label>Created From</label>
                                <router-link :to="{ name: 'order.show', params: { orderId: orderObject.created_from }}" class="block mt-3">{{ orderObject.created_from }}</router-link>
                            </div>
                        </div>
                    </div>

                    <div class="w-full md:w-1/2 md:pr-4">
                        <div class="account-body">
                            <div class="form-field" v-if="orderObject.original_order_billing">
                                <label>Payment Method</label>
                                <input type="text" :value="getPaymentProviderName" readonly="readonly">
                            </div>

                            <div class="form-field" v-if="orderObject.original_order_billing">
                                <label>Payment Token</label>
                                <input type="text" v-model="orderObject.original_order_billing.transaction_id" readonly="readonly">
                            </div>

                            <div class="form-field" v-if="orderObject.original_order_billing">
                                <label>Receipt</label>
                                <a :href="getReceiptLink" class="block mt-3" target="_blank">Download Receipt</a>
                            </div>

                            <div class="form-field" v-if="orderObject.original_order_billing && orderObject.original_order_billing.provider.name === 'Quickbooks'">
                                <label>Invoice</label>
                                <a :href="getInvoiceLink" class="block mt-3" target="_blank">Download Invoice</a>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="account-body">
                    <h2>Order Details</h2>

                    <div class="results-list">
                        <div class="results-row">
                            <div class="results-cell" data-width="80%">{{ orderObject.description }}</div>
                            <div class="results-cell text-right" data-width="20%">{{ formatCurrency(orderObject.net_cost_without_promotion) }}</div>
                        </div>

                        <div class="results-row" v-if="orderObject.promotion_net_cost > 0">
                            <div class="results-cell" data-width="80%">Promotion ({{ orderObject.promotion.code }})</div>
                            <div class="results-cell text-right" data-width="20%">({{ formatCurrency(orderObject.promotion_net_cost) }})</div>
                        </div>

                        <div class="results-row">
                            <div class="results-cell font-bold" data-width="80%">Subtotal</div>
                            <div class="results-cell text-right font-bold" data-width="20%">{{ formatCurrency(orderObject.net_cost) }}</div>
                        </div>

                        <div class="results-row">
                            <div class="results-cell" data-width="80%">VAT @ {{ (orderObject.tax_rate.tax_rate * 100).toFixed(2) }}%</div>
                            <div class="results-cell text-right" data-width="20%">{{ formatCurrency(orderObject.tax) }}</div>
                        </div>

                        <div class="results-row">
                            <div class="results-cell font-bold" data-width="80%">Total</div>
                            <div class="results-cell text-right font-bold" data-width="20%">{{ formatCurrency(orderObject.gross_cost) }}</div>
                        </div>

                        <div class="results-row" v-if="orderObject.credits.length > 0">
                            <div class="results-cell" data-width="80%">Account Credit</div>
                            <div class="results-cell text-right" data-width="20%">({{ formatCurrency(totalCreditSum) }})</div>
                        </div>

                        <div class="results-row" v-if="orderObject.credits.length > 0">
                            <div class="results-cell font-bold" data-width="80%">For payment</div>
                            <div class="results-cell text-right font-bold" data-width="20%">{{ formatCurrency(orderObject.gross_cost - totalCreditSum) }}</div>
                        </div>
                    </div>

                </div>
            </div>

            <div class="modal-footer">
                <div class="flex">
                    <div class="left text-left">
                        <!-- Maintaned as a reminder to implement -->
                        <!--<button class="btn-primary muted" type="button">Save Receipt</button>
                        <button class="btn-primary muted" type="button">Email Receipt</button>-->

                        <button
                            class="btn-danger muted"
                            type="button"
                            @click="handleRefundRequest"
                            v-if="shouldShowRefundButton"
                        >Refund</button>
                    </div>

                    <div class="right text-right">
                        <button class="btn-tertiary muted" type="button" @click="closeView">Close</button>

                        <button
                            class="btn-primary"
                            type="button"
                            @click=""
                            v-if="shouldShowSaveButton"
                        >Save</button>
                    </div>
                </div>
            </div>
        </div>

        <mini-modal name="orderRefund" title="Process Refund" :overlay="true">
            <div class="alert danger">
                This function is to record a refund and will not communicate with the provider. Ensure refund action is taken before recording.
            </div>
            <div class="form-field">
                <label>Refund Amount</label>
                <input type="text" v-model="refundAmount">
            </div>

            <div class="form-field">
                <label>Reason</label>
                <input type="text">
            </div>

            <div class="form-field">
                <button type="button" class="btn-danger" @click="handleProcessRefund">Process Refund</button> <button type="button" class="btn-tertiary muted" @click="closeRefundModal">Close</button>
            </div>
        </mini-modal>
    </div>
</template>

<script>
    import MiniModal from '../../MiniModal'

    export default {
        name: 'OrderView',
        props: {
            //
        },
        components: {
            MiniModal,
        },
        data() {
            return {
                orderObject: null,
                refundAmount: 0,
                changingAttribution: false,
                newAttribution: null,
            }
        },
        mounted() {
            this.loadData()

            this.setMetaTitle('View Order | JournoLink Control');
            window.addEventListener('keyup', this.handleKeypress)
        },
        beforeDestroy() {
            window.removeEventListener('keyup', this.handleKeypress)
        },
        computed: {
            /**
             * Determine if the "Save" button should be shown
             *
             * @return {Boolean}
             */
            shouldShowSaveButton() {
                return false
            },
            /**
             * Determine if the "Refund" button should be shown
             *
             * @return {Boolean}
             */
            shouldShowRefundButton() {
                return this.orderObject.gross_cost > 0 && this.orderObject.complete !== null
            },
            /**
             * Return the sunm of the order credits used on this Order
             *
             * @return {Float}
             */
            totalCreditSum() {
                return this.orderObject.credits.reduce((carry, credit) => {
                    return parseFloat(carry) + parseFloat(credit.value)
                }, 0).toFixed(2)
            },
            /**
             * Get the sale attribution from the order
             *
             * @return {String}
             */
            getSaleAttribution() {
                if (this.orderObject.attribution) {
                    if (this.orderObject.attribution.email) {
                        return this.orderObject.attribution.email
                    }
                }

                return ''
            },
            /**
             * Return the link to download a receipt for this Order
             *
             * @return {String}
             */
            getReceiptLink() {
                return route('orders.receipt', this.orderObject.id)
            },
            /**
             * Return the link to download a invoice for this Order
             *
             * @return {String}
             */
            getInvoiceLink() {
                return route('orders.invoice', this.orderObject.id)
            },
            /**
             * Return the name of the payment provider
             *
             * @return {String}
             */
            getPaymentProviderName() {
                if (this.orderObject.original_order_billing.provider) {
                    return this.orderObject.original_order_billing.provider.name
                }

                return 'Unknown'
            }
        },
        methods: {
            loadData() {
                axios.get(route('orders.show', this.$route.params.orderId))
                    .then(response => {
                        this.orderObject = response.data
                    })
            },
            /**
             * Emit the close event to the parent
             *
             * @return {void}
             */
            closeView() {
                this.$router.go(-1)
            },
            /**
             * Format currency value for output
             *
             * @param  {Float} value
             * @return {String}
             */
            formatCurrency(value) {
                if (value < 0) {
                    return '(' + Math.abs(value).toFixed(2) + ')'
                }

                return Math.abs(value).toFixed(2)
            },
            /**
             * Handle a refund click event
             *
             * @return {void}
             */
            handleRefundRequest() {
                this.refundAmount = (this.orderObject.gross_cost - this.totalCreditSum).toFixed(2)
                this.$bus.$emit('minimodal.show', 'orderRefund')
            },
            /**
             * Close the refund modal
             *
             * @return {void}
             */
            closeRefundModal() {
                this.refundAmount = 0
                this.$bus.$emit('minimodal.hide', 'orderRefund')
            },
            /**
             * Handle a request to process a refund against an order
             *
             * @return {void}
             */
            handleProcessRefund() {
                axios.post(route('orders.refund', this.orderObject.id), {
                    amount: this.refundAmount,
                })
                    .then(response => {
                        this.$bus.$emit('alert.success', 'Refund has been recorded')
                        this.$bus.$emit('account.reload')
                        this.closeRefundModal()
                    })
                    .catch((e) => {
                        console.error(e)
                    })
            },
            /**
             * Handle a click event to change the order attribution
             *
             * @return  {void}
             */
            handleChangeOrderAttribution() {
                this.changingAttribution = true
            },
            /**
             * Change the attribution of an Order
             *
             * @return  {[type]}
             */
            changeOrderAttribution() {
                axios.put(route('orders.update', this.orderObject.id), {
                    attribution_type: 'JournoLink\\Shared\\Models\\User',
                    attribution_id: this.orderObject.attribution_id,
                })
                    .then(response => {
                        this.$bus.$emit('alert.success', 'Order attribution has been updated')
                        this.$bus.$emit('account.reload')
                        this.orderObject = response.data.order
                    })
                    .catch((e) => {
                        console.error(e)
                    })

                this.changingAttribution = false
            },
            /**
             * Handle an [Escape] keypress
             *
             * @return {void}
             */
            onEscapeKeypress() {
                this.closeView()
            },
        }
    }
</script>
